import React, { useEffect, useMemo, useRef, useState } from 'react';
import { func, string, bool, shape, arrayOf, oneOf } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import { useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import { propTypes } from '../../../../util/types';
import {
  createSlug,
  combineSearchParams,
  combineCategorySearch,
} from '../../../../util/urlHelpers';
import { FormattedMessage, injectIntl, intlShape } from '../../../../util/reactIntl';
import {
  createFilterOptions,
  filterCategoryTree,
  addDisplayNameToCategories,
} from '../../../../util/search';
import { fetchTopbarListings } from '../../../../ducks/search.duck';
import { useConfiguration } from '../../../../context/configurationContext';
import { Form, InlineTextButton, NamedLink, IconSpinner } from '../../../../components';

import IconSearch from './IconSearch';
import css from './TopbarSearchForm.module.css';

const applySearchFilters = categories =>
  categories.map(category => ({ ...category, search: `?pub_categoryLevel1=${category.id}` }));

const filterBrands = (brands, query) =>
  brands.filter(brand => brand.label.toLowerCase().includes(query.toLowerCase()));

const matchingIdentifier = (listing, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) {
    return null;
  }
  const { publicData = {} } = listing.attributes || {};
  const candidates = [publicData.ean, publicData.mpn, listing.id.uuid];
  return candidates.find(c => c && String(c).toLowerCase() === q) || null;
};

const KeywordPredictionsList = props => {
  const {
    className,
    query,
    appConfig,
    currentSearchParams,
    searchProps,
    topbarListings,
    fetchTopbarListingsInProgress,
    fetchTopbarListingsError,
  } = props;
  const { categories: listingCategoriesConfig } = appConfig.categoryConfiguration;
  const { listingFields } = appConfig.listing;

  const linkProps = searchProps ? searchProps : { name: 'SearchPage' };
  const isProfilePage = linkProps.name === 'ProfilePage';

  const brandConfig = listingFields.find(f => f.key === 'brand');
  const brandOptions = brandConfig ? createFilterOptions(brandConfig.enumOptions) : [];

  // Define a maximum limit for categories, brands, and products
  const maxResults = 5;

  let filteredCategories = [];

  if (query === '') {
    // If no query, just take the first 5 top-level categories (no subcategories)
    const filteredCategoriesWithSearchFilters = applySearchFilters(listingCategoriesConfig);
    filteredCategories = filteredCategoriesWithSearchFilters.slice(0, maxResults);
  } else {
    // If there is a query, filter and then limit the results to 5
    const filteredCategoriesWithDisplayName = addDisplayNameToCategories(listingCategoriesConfig);
    filteredCategories = filterCategoryTree(filteredCategoriesWithDisplayName, query).slice(
      0,
      maxResults
    );
  }

  const filteredBrands = filterBrands(brandOptions, query).slice(0, maxResults);
  // topbarListings is already scoped to `query` by the catalog API (see
  // fetchTopbarListings) — filtering again here on title alone would drop the
  // rows it matched on description or brand.
  const filteredProducts = topbarListings.slice(0, maxResults);

  const renderCategories = categories =>
    categories.map((category, index) => {
      const combinedSearch = combineCategorySearch(currentSearchParams, category.search);
      return (
        <li key={`${category.id}_${index}`} className={css.predictionsItem}>
          <NamedLink
            className={css.predictionsItemLink}
            {...linkProps}
            to={{ search: combinedSearch }}
          >
            {category.displayName || category.name}
          </NamedLink>
        </li>
      );
    });

  const classes = classNames(css.predictions, className);

  return (
    <div className={classes}>
      {/* Brands Section */}
      <ul className={css.predictionsList}>
        <li className={classNames(css.predictionsItem, css.highlighted)}>
          <FormattedMessage id="TopbarSearchForm.brandsTitle" />
        </li>
        {filteredBrands.length > 0 ? (
          filteredBrands.map(brand => {
            const combinedSearch = combineSearchParams(currentSearchParams, {
              pub_brand: brand.key,
            });
            return (
              <li key={brand.key} className={css.predictionsItem}>
                <NamedLink
                  className={css.predictionsItemLink}
                  {...linkProps}
                  to={{ search: combinedSearch }}
                >
                  {brand.label}
                </NamedLink>
              </li>
            );
          })
        ) : (
          <li className={css.predictionsItem}>
            <FormattedMessage id="TopbarSearchForm.noResultsForBrands" />
          </li>
        )}
      </ul>
      {/* Categories Section */}
      <ul className={css.predictionsList}>
        <li className={classNames(css.predictionsItem, css.highlighted)}>
          <FormattedMessage id="TopbarSearchForm.categoriesTitle" />
        </li>
        {filteredCategories.length > 0 ? (
          renderCategories(filteredCategories)
        ) : (
          <li className={css.predictionsItem}>
            <FormattedMessage id="TopbarSearchForm.noResultsForCategories" />
          </li>
        )}
      </ul>
      {/* Products Section Maybe */}
      {!isProfilePage && (
        <ul className={css.predictionsList}>
          <li className={classNames(css.predictionsItem, css.highlighted)}>
            <FormattedMessage id="TopbarSearchForm.productsTitle" />
          </li>
          {fetchTopbarListingsInProgress ? (
            <li className={classNames(css.predictionsItem, css.loading)}>
              <IconSpinner className={css.spinnerIcon} />
            </li>
          ) : fetchTopbarListingsError ? (
            <li className={classNames(css.predictionsItem, css.error)}>
              <FormattedMessage id="TopbarSearchForm.errorFetchingProducts" />
            </li>
          ) : filteredProducts.length > 0 ? (
            filteredProducts.map(listing => {
              const matchedCode = matchingIdentifier(listing, query);
              return (
                <li key={listing.id.uuid} className={css.predictionsItem}>
                  <NamedLink
                    className={css.predictionsItemLink}
                    name="ListingPage"
                    params={{
                      id: listing.id.uuid,
                      slug: createSlug(listing.attributes.title),
                    }}
                  >
                    {listing.attributes.title}
                    {matchedCode ? <span className={css.predictionCode}>{matchedCode}</span> : null}
                  </NamedLink>
                </li>
              );
            })
          ) : (
            <li className={css.predictionsItem}>
              <FormattedMessage id="TopbarSearchForm.noResultsForProducts" />
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const TopbarSearchFormComponent = props => {
  const { onSubmit, ...restOfProps } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [query, setQuery] = useState('');

  const searchInputRef = useRef(null);
  const appConfig = useConfiguration();
  const dispatch = useDispatch();

  // Product predictions are fetched per keystroke against the full catalog,
  // so debounce to one request per typing pause rather than one per character.
  const fetchPredictions = useMemo(
    () => debounce(search => dispatch(fetchTopbarListings(search)), 300),
    [dispatch]
  );
  useEffect(() => () => fetchPredictions.cancel(), [fetchPredictions]);

  // The blur handler waits 100 ms; leaving the page in between (e.g. submitting a
  // search) unmounts the form, and the late setState warned about a leak.
  const blurTimer = useRef(null);
  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = event => {
    // Use setTimeout to delay the blur logic
    clearTimeout(blurTimer.current);
    blurTimer.current = setTimeout(() => {
      // Prevent blur logic if the next focus target is inside the predictions list
      if (
        event.relatedTarget &&
        event.relatedTarget.closest &&
        event.relatedTarget.closest(`.${css.predictions}`)
      ) {
        return;
      }
      setIsFocused(false);
    }, 100);
  };

  const handleInputChange = (event, input) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
    fetchPredictions(newQuery);
    input.onChange(event);
  };

  const handleSubmit = values => {
    onSubmit({ keywords: values.keywords });
    searchInputRef.current?.blur();
  };

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={handleSubmit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          inputClassName,
          inputRootClassName,
          predictionsClassName,
          intl,
          inputPlaceholder,
          currentSearchParams,
          searchProps,
          topbarListings,
          fetchTopbarListingsInProgress,
          fetchTopbarListingsError,
          handleSubmit,
        } = formRenderProps;
        const classes = classNames(rootClassName || className, css.root);
        const inputClasses = classNames(css.input, inputClassName);
        const inputRootClasses = classNames(css.inputRoot, inputRootClassName);

        // Deliberately static. This used to interpolate the live listing count,
        // which the server has no way to know at render time — so the placeholder
        // shipped as "Search products, brands, sellers" and swapped to the counted
        // version the instant hydration finished, visibly flickering in the search
        // bar on every page load.
        // NOTE: deliberately a different key from `TopbarSearchForm.placeholder`.
        // Sharetribe's hosted translations override local ones (see app.js:
        // `{ ...localeMessages, ...hostedTranslations }`), and the hosted copy of
        // that key still carries the `{count}` ICU pattern — editing en.json alone
        // has no effect, and rendering it without a `count` arg prints the raw
        // pattern. This key does not exist in the hosted asset, so local wins.
        const placeholder =
          inputPlaceholder || intl.formatMessage({ id: 'TopbarSearchForm.placeholderStatic' });
        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="SearchPage">
            <div className={css.keywordSearchWrapper}>
              <InlineTextButton className={css.searchSubmit}>
                <IconSearch />
              </InlineTextButton>
              <Field name="keywords">
                {({ input }) => (
                  <div className={inputClasses}>
                    <input
                      {...input}
                      className={inputRootClasses}
                      id="keyword-search"
                      ref={searchInputRef}
                      type="text"
                      placeholder={placeholder}
                      autoComplete="off"
                      value={query}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onChange={event => handleInputChange(event, input)}
                    />
                  </div>
                )}
              </Field>
            </div>
            {isFocused && (
              <KeywordPredictionsList
                className={predictionsClassName}
                query={query}
                appConfig={appConfig}
                currentSearchParams={currentSearchParams}
                searchProps={searchProps}
                topbarListings={topbarListings}
                fetchTopbarListingsInProgress={fetchTopbarListingsInProgress}
                fetchTopbarListingsError={fetchTopbarListingsError}
              />
            )}
          </Form>
        );
      }}
    />
  );
};

TopbarSearchFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputClassName: null,
  inputRootClassName: null,
  predictionsClassName: null,
  inputPlaceholder: null,
  fetchTopbarListingsInProgress: false,
  fetchTopbarListingsError: null,
};

TopbarSearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputClassName: string,
  inputRootClassName: string,
  predictionsClassName: string,
  inputPlaceholder: string,
  onSubmit: func.isRequired,
  searchProps: shape({
    name: oneOf(['ProfilePage', 'SearchPage']).isRequired,
    params: shape({
      id: string,
    }).isRequired,
  }),
  topbarListings: arrayOf(propTypes.listing),
  fetchTopbarListingsInProgress: bool.isRequired,
  fetchTopbarListingsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const TopbarSearchForm = injectIntl(TopbarSearchFormComponent);

export default TopbarSearchForm;
